import type { CellObject, WorkBook } from 'xlsx';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite resolves worker url
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = pdfWorker;

export async function loadXlsx(): Promise<typeof import('xlsx')> {
    // Dynamic import to avoid bundling if not used
    return import('xlsx');
}

export function findSheet(wb: WorkBook, candidates: string[]): string | null {
    for (const name of candidates) {
        const matched = wb.SheetNames.find((sheet) => sheet.toLowerCase().includes(name.toLowerCase()));
        if (matched) return matched;
    }
    return wb.SheetNames[0] ?? null;
}

export function readRows(sheet: any, xlsx: typeof import('xlsx')): (CellObject | string | number | null)[][] {
    const rows: (CellObject | string | number | null)[][] = xlsx.utils.sheet_to_json(sheet, {
        header: 1,
        raw: true,
        defval: null,
    }) as any;
    return rows;
}

export async function readFileAsTextWithFallback(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const utf8Decoder = new TextDecoder('utf-8', { fatal: false });
    let text = utf8Decoder.decode(buffer);
    if (text.includes('\uFFFD')) {
        const latinDecoder = new TextDecoder('latin1');
        text = latinDecoder.decode(buffer);
    }
    return text;
}

export function splitDelimitedLine(line: string, delimiter: string): string[] {
    const result: string[] = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
        const char = line[i] ?? '';
        if (char === '"') {
            const nextChar = line[i + 1] ?? '';
            if (nextChar === '"') {
                current += '"';
                i += 1;
            } else {
                insideQuotes = !insideQuotes;
            }
            continue;
        }
        if (!insideQuotes && delimiter && line.startsWith(delimiter, i)) {
            result.push(current);
            current = '';
            i += delimiter.length - 1;
            continue;
        }
        current += char;
    }

    result.push(current);
    return result.map((c) => c.trim());
}

export function serialToDate(value: any, xlsx: typeof import('xlsx')): string | null {
    if (typeof value !== 'number') return null;
    try {
        const date = xlsx.SSF.parse_date_code(value);
        if (!date) return null;
        const month = String(date.m).padStart(2, '0');
        const day = String(date.d).padStart(2, '0');
        return `${date.y}-${month}-${day}`;
    } catch {
        return null;
    }
}

export async function extractTextFromPdf(file: File, password?: string): Promise<string[]> {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer, password }).promise;
    const pages: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent({ normalizeWhitespace: true } as any);
        // Reconstrói linhas usando hasEOL (quando disponível), para aproximar do layout do pdftotext
        const yMap = new Map<number, { x: number; str: string }[]>();
        const Y_TOLERANCE = 5;

        for (const item of content.items as any[]) {
            const str = (item.str as string) ?? '';
            if (!str.trim()) continue;

            const transform = item.transform;
            const y = transform ? transform[5] : 0;
            let foundY: number | null = null;

            for (const existingY of Array.from(yMap.keys())) {
                if (Math.abs(existingY - y) < Y_TOLERANCE) {
                    foundY = existingY;
                    break;
                }
            }

            if (foundY === null) {
                yMap.set(y, [{ x: transform ? transform[4] : 0, str }]);
            } else {
                yMap.get(foundY)?.push({ x: transform ? transform[4] : 0, str });
            }
        }

        const sortedYs = Array.from(yMap.keys()).sort((a, b) => b - a);
        const lines: string[] = [];

        for (const y of sortedYs) {
            const items = yMap.get(y);
            if (!items) continue;
            items.sort((a, b) => a.x - b.x);
            lines.push(items.map((i) => i.str).join(' '));
        }
        pages.push(lines.join('\n'));
    }
    return pages;
}

export const BRA_CERTIFICATE_LINE_REGEX = /^(\d{6,}\/\d{2})\s+(.*?)\s*(\d{2}\/\d{2}\/\d{4})\s+(.*)$/;
export const BRA_FULL_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;
export const BRA_COMPETENCE_REGEX = /^(\d{2})\/(\d{4})$/;
export const BRA_RELATIONSHIP_LABELS: Record<string, string> = {
    TIT: 'Titular',
    TITULAR: 'Titular',
    FILH: 'Filho',
    FILHO: 'Filho',
    CONJ: 'Conjuge',
    CONJUGE: 'Conjuge',
    PAI: 'Pai',
    MAE: 'Mae',
};

export function parseBradescoDate(value: string | null | undefined): string | null {
    if (!value) return null;
    const match = value.trim().match(BRA_FULL_DATE_REGEX);
    if (!match) return null;
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
}

export function parseBradescoCompetence(value: string | null | undefined): string | null {
    if (!value) return null;
    const match = value.trim().match(BRA_COMPETENCE_REGEX);
    if (!match) return null;
    const [, month, year] = match;
    return `${year}-${month}`;
}

export function extractCpf(value: CellObject | string | number | null | undefined): string {
    if (value == null) return '';
    const raw = typeof value === 'number' ? value.toFixed(0) : value.toString();
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 0) return '';
    return digits.length === 11 ? digits : digits.padStart(11, '0');
}

import { HealthPlanLifeRecord, HealthPlanMovementRecord } from '../healthPlanTypes';

export function parseLives(rows: (CellObject | string | number | null)[][], xlsx: typeof import('xlsx')): HealthPlanLifeRecord[] {
    const headerIndex = rows.findIndex(
        (r) => (r[1]?.toString() ?? '').toLowerCase().includes('beneficiário') && (r[2]?.toString() ?? '').includes('CPF'),
    );
    if (headerIndex === -1) return [];
    const headerRow = rows[headerIndex] ?? [];
    const dataRows = rows.slice(headerIndex + 1);

    const userCodeIndex = headerRow.findIndex((cell) => (cell?.toString() ?? '').toLowerCase().includes('usu'));

    return dataRows
        .map((row) => {
            const userCodeRaw = userCodeIndex >= 0 ? row[userCodeIndex] : row[0];
            const userCode =
                typeof userCodeRaw === 'number' ? userCodeRaw.toFixed(0) : userCodeRaw?.toString().trim() ?? null;
            const beneficiaryName = row[1]?.toString().trim() ?? '';
            const beneficiaryCpf = extractCpf(row[2]);
            const holderCpf = extractCpf(row[3]);
            const birthDate = serialToDate(row[4], xlsx);
            const relationship = row[6]?.toString().trim() ?? '';
            const planName = row[7]?.toString().trim() ?? '';
            const inclusionDate = serialToDate(row[8], xlsx);
            const premiumTotal = typeof row[10] === 'number' ? row[10] : null;
            const copayAmount = typeof row[13] === 'number' ? row[13] : null;

            if (!beneficiaryName) return null;
            const normalizedName = beneficiaryName.toLowerCase();
            if (normalizedName.startsWith('benefici') || normalizedName.startsWith('titular')) return null;

            const premium = typeof row[10] === 'number' ? row[10] : null;
            const copay = typeof row[13] === 'number' ? row[13] : null;
            return {
                beneficiaryName,
                beneficiaryCpf,
                holderCpf,
                userCode: normalizeUserCode(userCode),
                birthDate,
                relationship,
                planName,
                inclusionDate,
                premiumTotal: premium,
                copayAmount: copay,
                totalCost: (premium ?? 0) + (copay ?? 0),
                plans: [
                    {
                        planName,
                        premiumTotal: premium,
                        copayAmount: copay,
                    },
                ],
                copayEntries: [],
            } as HealthPlanLifeRecord;
        })
        .filter(Boolean) as HealthPlanLifeRecord[];
}

export function consolidateLives(records: HealthPlanLifeRecord[]): HealthPlanLifeRecord[] {
    const map = new Map<string, HealthPlanLifeRecord>();

    records.forEach((record) => {
        const nameKey = normalizeName(record.beneficiaryName);
        const beneficiaryCpf = normalizeCpf(record.beneficiaryCpf);
        const holderCpf = normalizeCpf(record.holderCpf);
        const key = beneficiaryCpf || (holderCpf && nameKey ? `${holderCpf}|${nameKey}` : nameKey);

        const existing = map.get(key);
        if (!existing) {
            map.set(key, record);
            return;
        }

        const mergedPlanNames = Array.from(
            new Set(
                [existing.planName, record.planName].filter(Boolean),
            ),
        ).join(' / ');

        const premiumTotal = (existing.premiumTotal ?? 0) + (record.premiumTotal ?? 0);
        const copayAmount = (existing.copayAmount ?? 0) + (record.copayAmount ?? 0);
        map.set(key, {
            ...existing,
            premiumTotal,
            copayAmount,
            totalCost: premiumTotal + copayAmount,
            planName: mergedPlanNames || existing.planName || record.planName,
            inclusionDate: existing.inclusionDate ?? record.inclusionDate ?? null,
            relationship: existing.relationship || record.relationship,
            holderCpf: existing.holderCpf || record.holderCpf,
            beneficiaryCpf: existing.beneficiaryCpf || record.beneficiaryCpf,
            userCode: existing.userCode || record.userCode,
            plans: [...(existing.plans ?? []), ...(record.plans ?? [])],
        });
    });

    return Array.from(map.values());
}

export function parseMovements(
    rows: (CellObject | string | number | null)[][],
    xlsx: typeof import('xlsx'),
): HealthPlanMovementRecord[] {
    const headerIndex = rows.findIndex((r) => (r[1]?.toString() ?? '').toLowerCase().includes('tipo da movimentação'));
    if (headerIndex === -1) return [];
    const dataRows = rows.slice(headerIndex + 1);

    return dataRows
        .map((row) => {
            const movementType = row[1]?.toString().trim() ?? '';
            const beneficiaryName = row[2]?.toString().trim() ?? '';
            const beneficiaryCpf = row[3]?.toString().trim() ?? '';
            const holderCpf = row[4]?.toString().trim() ?? '';
            const relationship = row[5]?.toString().trim() ?? '';
            const planName = row[6]?.toString().trim() ?? '';
            const inclusionDate = serialToDate(row[7], xlsx);
            const exclusionDate = serialToDate(row[8], xlsx);
            const movementCompetence = row[9]?.toString().trim() ?? null;
            const value = typeof row[10] === 'number' ? row[10] : null;

            if (!movementType || !beneficiaryName) return null;

            return {
                movementType,
                beneficiaryName,
                beneficiaryCpf,
                holderCpf,
                relationship,
                planName,
                inclusionDate,
                exclusionDate,
                movementCompetence,
                value,
            } as HealthPlanMovementRecord;
        })
        .filter(Boolean) as HealthPlanMovementRecord[];
}

export const COPAY_PREFIXES = [
    'CUSTO_BH',
    'CUSTO_INT',
    'COPAR_BH',
    'COPAR_INT',
    'INDEV_BH',
    'INDEV_INT',
    'PCMSO_BH',
    'PCMSO_INT',
    'IPSIBH_EPP',
    'PSIBH_IPP',
    'PSBH_ECOP',
    'PSBH_ICOP',
    'PSIN_ECO',
    'PSIIN_EPP',
    'PSIN_ICOP',
    'PSIIN_IPP',
    'PSIIN_ICO',
    'PSIBH_ICO',
];

export function normalizeCpf(value?: string | null): string {
    if (!value) return '';
    return value.replace(/\D/g, '');
}

export function normalizeUserCode(value?: string | null): string {
    if (!value) return '';
    return value.toString().replace(/\D/g, '');
}

export function digitsOnly(value?: string | null): string {
    if (!value) return '';
    return value.replace(/\D/g, '');
}

export function toNumber(value: string | null | undefined): number | null {
    if (!value) return null;
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

export function toInt(value: string | null | undefined): number | null {
    if (!value) return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
}

export function toSignedCurrency(value: string | null | undefined): number | null {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    const isNegative = trimmed.endsWith('-') || trimmed.startsWith('-');
    const normalized = trimmed.replace(/-/g, '');
    const parsed = toNumber(normalized);
    if (parsed == null) return null;
    return isNegative ? -parsed : parsed;
}

export function normalizeName(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

import type { HealthPlanCopayRecord } from '../healthPlanTypes';

export function parseCopayFromText(textPages: string[]): HealthPlanCopayRecord[] {
    const prefixSet = new Set(COPAY_PREFIXES);
    const entries: HealthPlanCopayRecord[] = [];

    const parseLineTokens = (
        tokens: string[],
        holder: { name: string | null; userCode: string | null },
    ): HealthPlanCopayRecord[] => {
        const parsed: HealthPlanCopayRecord[] = [];
        const isNumber = (t: string) => /^[0-9]+(?:[.,][0-9]{1,2})?$/.test(t);
        const popNumber = (idx: number) => (idx >= 0 && isNumber(tokens[idx]) ? tokens[idx] : null);

        // percorre tokens procurando prefixo
        for (let i = 0; i < tokens.length; i += 1) {
            const token = tokens[i];
            if (!prefixSet.has(token)) continue;

            let idx = i - 1;
            const nameTokens: string[] = [];
            while (idx >= 0 && !isNumber(tokens[idx])) {
                nameTokens.unshift(tokens[idx]);
                idx -= 1;
            }

            const taxa = popNumber(idx--);
            const flag = tokens[idx] ?? null;
            idx -= 1;
            const valor = popNumber(idx--);
            const filmM2 = popNumber(idx--);
            const qty2 = tokens[idx] ?? null;
            idx -= 1;
            const pct = tokens[idx] ?? null;
            idx -= 1;
            const qty = tokens[idx] ?? null;
            idx -= 1;
            const partic = tokens[idx] ?? null;
            idx -= 1;

            const descTokens: string[] = [];
            while (idx >= 0 && !/^\d+$/.test(tokens[idx])) {
                descTokens.unshift(tokens[idx]);
                idx -= 1;
            }
            const productCode = tokens[idx] ?? null;
            idx -= 1;
            const time = tokens[idx] ?? null;
            idx -= 1;
            const date = tokens[idx] ?? null;
            idx -= 1;
            const pedido = tokens[idx] ?? null;
            idx -= 1;
            const solicitacao = tokens[idx] ?? null;
            idx -= 1;
            const provider = tokens[idx] ?? null;
            idx -= 1;
            const userCode = tokens[idx] ?? null;

            const description = descTokens.join(' ').trim() || null;
            const raw = [
                userCode,
                provider,
                solicitacao,
                pedido,
                date,
                time,
                productCode,
                description,
                partic,
                qty,
                pct,
                qty2,
                filmM2,
                valor,
                flag,
                taxa,
                nameTokens.join(' '),
                token,
            ]
                .filter(Boolean)
                .join(' ');
            const descFromRawMatch = raw.match(
                /^\s*\d{6,}\s+\d{6,}\s+\d+\s+\d+\s+\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}\s+\d+\s+(.+?)\s+\d+\s+\d+\s+\d+\s+\d+\s+[0-9.,]+\s+[0-9.,]+\s+[SN]/,
            );
            const parsedDescription = descFromRawMatch?.[1]?.trim() || description;
            const value = toNumber(valor ?? taxa);
            if (!userCode || !provider || !productCode || !date || !time || value == null) continue;

            parsed.push({
                type: token,
                beneficiaryName: nameTokens.join(' ') || 'Desconhecido',
                beneficiaryCpf: null,
                holderCpf: null,
                holderName: holder.name,
                holderUserCode: holder.userCode,
                userCode: normalizeUserCode(userCode),
                providerCode: provider,
                requestCode: solicitacao ?? null,
                orderNumber: pedido ?? null,
                productCode: productCode ?? null,
                description: parsedDescription,
                procedure: parsedDescription,
                participation: toInt(partic),
                quantity: toInt(qty),
                percentage: toInt(pct),
                additionalQuantity: toInt(qty2),
                filmArea: toNumber(filmM2),
                filmValue: null,
                value,
                flag,
                serviceDate: `${date} ${time}`,
                serviceTime: time,
                raw,
            });
        }

        return parsed;
    };

    let currentHolder: { name: string | null; userCode: string | null } = { name: null, userCode: null };

    for (const pageText of textPages) {
        const lines = pageText.split('\n');
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            const titularMatch = trimmed.match(/Titular:\s*(\d{6,})\s+(.+?)(?:\s+Matr[ií]cula:|\s+Tipo|$)/i);
            let updatedHolder = false;
            if (titularMatch) {
                currentHolder = { userCode: titularMatch[1], name: titularMatch[2]?.trim() ?? null };
                updatedHolder = true;
            }

            if (trimmed.startsWith('Total Família:') && !updatedHolder) {
                currentHolder = { name: null, userCode: null };
                continue;
            }

            // pula linhas de legenda com prefixos seguidos de dois-pontos
            if (COPAY_PREFIXES.some((p) => trimmed.startsWith(`${p}:`))) continue;

            if (COPAY_PREFIXES.some((p) => trimmed.includes(p))) {
                const lineTokens = trimmed.split(/\s+/).filter(Boolean);
                const parsed = parseLineTokens(lineTokens, currentHolder);
                entries.push(...parsed);
            }
        }
    }

    return entries;
}
